import React from "react";
import { MapPin } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { PricePill } from "../PricePill";
import FeedGoingPreview from "../feed/FeedGoingPreview";
import { EventCardMeta, EventCardTitle, EventCardSubtitle } from "../ui/EventCard";

/**
 * Home demo event row — same quiet-card shell as feed / EventCard so host themes apply.
 * Text-first: date → title → people → place. Whole row is the action.
 * No cover thumb — we only have cover_color gradients, not photos.
 */
export default function DemoLandingCard({
  eyebrow,
  title,
  subtitle,
  stepsLabel,
  location,
  whenLabel,
  chips,
  goingPreview,
  goingTotal,
  pricing,
  rsvpCount,
  cta,
  ctaColor = "accent",
  onCta,
  featured = false,
  testId,
  ctaTestId,
  className = "",
}) {
  // ctaColor kept for call-site compatibility; underline uses themed ink tokens.
  void ctaColor;
  const metaLine = [eyebrow, whenLabel].filter(Boolean).join(" · ");
  const detailLine = [
    stepsLabel,
    ...(chips || []).map((c) => c.label),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <article
      className={twMerge("group relative", className)}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={onCta}
        data-testid={ctaTestId}
        aria-label={cta ? `${title}. ${cta}` : title}
        className={twMerge(
          "w-full text-left quiet-card quiet-card-interactive focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink/30 focus-visible:outline-offset-2",
          featured ? "p-5 sm:p-6" : "p-4 sm:p-5",
        )}
      >
        {metaLine ? <EventCardMeta>{metaLine}</EventCardMeta> : null}

        <EventCardTitle featured={featured}>{title}</EventCardTitle>

        {subtitle ? <EventCardSubtitle>{subtitle}</EventCardSubtitle> : null}

        {goingPreview?.length ? (
          <FeedGoingPreview
            preview={goingPreview}
            total={goingTotal || goingPreview.length}
            className="mt-3"
          />
        ) : null}

        {detailLine ? (
          <p className="mt-2 font-ui text-[12px] font-medium text-ink/50 truncate">
            {detailLine}
          </p>
        ) : null}

        <div className="mt-3 flex items-center gap-x-3 gap-y-1 flex-wrap min-w-0">
          {location ? (
            <p className="inline-flex items-center gap-1 min-w-0 font-ui text-[12px] sm:text-[13px] font-medium text-ink/50">
              <MapPin className="w-3.5 h-3.5 shrink-0 opacity-70" strokeWidth={2} aria-hidden />
              <span className="truncate">{location}</span>
            </p>
          ) : null}
          {pricing ? <PricePill pricing={pricing} rsvpCount={rsvpCount} /> : null}
        </div>

        {cta ? (
          <p
            className="mt-3 inline-block font-ui text-[13px] font-medium text-ink/70 underline decoration-2 underline-offset-[4px] decoration-ink/40 group-hover:text-ink group-hover:decoration-ink/70 transition-colors"
          >
            {cta}
          </p>
        ) : null}
      </button>
    </article>
  );
}
